// Public invite acceptance: an invited org admin / instructor / student lands
// here from their email link, sets a password, and is signed in. No access code
// needed; the invite IS the authorization.
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { BRAND } from '@/lib/brand';

const ROLE_LABEL = { org_admin: 'administrator', instructor: 'instructor', student: 'student' };

function Field({ id, label, value, onChange, testid }) {
  return (
    <div>
      <label htmlFor={id} className="mono-stat block">{label}</label>
      <input
        id={id}
        type="password"
        data-testid={testid}
        value={value}
        onChange={onChange}
        required
        className="mt-2 w-full border border-slate-300 bg-white px-3.5 py-2.5 text-[15px] text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-slate-900"
      />
    </div>
  );
}

export default function AcceptInvite() {
  const { signIn } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | invalid
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    api.inviteInfo(token)
      .then((res) => { setInfo(res.data); setStatus('ready'); })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      const res = await api.acceptInvite(token, password);
      signIn(res.data.token, res.data.user);
      // Staff roles run an organization, not a study plan, so land them there.
      const role = res.data.user?.role;
      navigate(role === 'org_admin' || role === 'instructor' ? '/org?welcome=1' : '/');
    } catch (err) {
      setError(err.response?.data?.detail || 'This invite link is invalid or already used.');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto grid min-h-screen max-w-6xl lg:grid-cols-2">
        <div className="hidden flex-col justify-between border-r border-slate-200 p-12 lg:flex">
          <p className="h-display text-xl">{BRAND}<span className="text-indigo-600">.</span></p>
          <div>
            <p className="mono-stat">CELPIP · PTE Core</p>
            <h1 className="h-display mt-4 text-5xl leading-[1.04]">Set your password to get started.</h1>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-slate-500">
              You've been added to an organization on the platform. Choose a password and you're in.
            </p>
          </div>
          <p className="mono-stat">Invitation</p>
        </div>

        <div className="flex items-center p-6 lg:p-12">
          <div className="w-full max-w-sm">
            <p className="h-display mb-8 text-xl lg:hidden">{BRAND}<span className="text-indigo-600">.</span></p>

            {status === 'loading' && (
              <div className="space-y-3" data-testid="invite-loading">
                <div className="h-3 w-24 animate-pulse bg-slate-200" />
                <div className="h-7 w-56 animate-pulse bg-slate-200" />
                <div className="mt-8 h-11 w-full animate-pulse bg-slate-100" />
                <div className="h-11 w-full animate-pulse bg-slate-100" />
              </div>
            )}

            {status === 'invalid' && (
              <div data-testid="invite-invalid">
                <p className="mono-stat">Invitation</p>
                <h2 className="h-display mt-2 text-2xl">This link isn't valid.</h2>
                <p className="mt-4 text-[15px] leading-relaxed text-slate-500">
                  It may have already been used or expired. Ask whoever invited you to send a fresh link.
                </p>
                <button type="button" className="cta-quiet mt-6 px-0" onClick={() => navigate('/login')}>
                  Go to sign in
                </button>
              </div>
            )}

            {status === 'ready' && info && (
              <>
                <p className="mono-stat">{info.org_name}</p>
                <h2 className="h-display mt-2 text-2xl">Welcome, {info.name}.</h2>
                <p className="mt-2 text-[15px] text-slate-500">
                  You're joining <span className="font-medium text-slate-700">{info.org_name}</span> as
                  {' '}an {ROLE_LABEL[info.role] || 'member'}. Set a password for <span className="font-medium text-slate-700">{info.email}</span>.
                </p>
                <form onSubmit={submit} className="mt-8 space-y-5">
                  <Field id="password" label="New password" value={password} onChange={(e) => setPassword(e.target.value)} testid="invite-password" />
                  <Field id="confirm" label="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} testid="invite-confirm" />
                  {error && (
                    <p className="border-l-2 border-red-500 pl-3 text-sm text-red-600" data-testid="invite-error">{error}</p>
                  )}
                  <button type="submit" disabled={busy} className="cta w-full justify-center disabled:opacity-50" data-testid="invite-submit">
                    {busy ? 'Setting up' : 'Set password and continue'}
                    {!busy && <ArrowRight className="h-4 w-4" />}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
